import React, { useCallback, useEffect, useState } from "react";
import { UserCog, Trash2, Link2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Label, Select, Check } from "./FormBits";
import { CATEGORIES, PLATFORMS, errMsg } from "./marketingConstants";

const STATUS_COPY = {
  pending: "Pending admin review — not public",
  approved: "Approved — visible in the showcase",
  rejected: "Not approved — not public",
};

function toForm(p) {
  const links = {};
  (p.social_links || []).forEach((s) => { links[s.platform] = s.url; });
  return { display_name: p.display_name || "", bio: p.bio || "", avatar_url: p.avatar_url || "",
    featured_content_url: p.featured_content_url || "", category: p.category || "other",
    social_links: links, consent_public: !!p.consent_public, show_audience_figures: !!p.show_audience_figures };
}

function ProfileEditor({ p, hasAffiliate, reload }) {
  const [f, setF] = useState(() => toForm(p));
  const [msg, setMsg] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const act = async (fn, ok) => { setMsg(""); try { await fn(); setMsg(ok); reload(); } catch (e) { setMsg(errMsg(e, "Action failed")); } };

  return (
    <div className="glass-panel p-5 space-y-3" data-testid={`my-profile-${p.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-bebas uppercase tracking-wider text-white">{p.display_name}</div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-lifted-green" data-testid="my-profile-status">{STATUS_COPY[p.status] || p.status}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Display name" value={f.display_name} onChange={(v) => set("display_name", v)} testid="mp-display-name" />
        <Select label="Category" value={f.category} onChange={(v) => set("category", v)} options={CATEGORIES} testid="mp-category" />
      </div>
      <div>
        <Label>Bio</Label>
        <textarea className="input-lifted w-full min-h-[60px] text-sm" maxLength={400} value={f.bio} onChange={(e) => set("bio", e.target.value)} data-testid="mp-bio" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Avatar URL" value={f.avatar_url} onChange={(v) => set("avatar_url", v)} testid="mp-avatar" />
        <Field label="Featured content URL" value={f.featured_content_url} onChange={(v) => set("featured_content_url", v)} testid="mp-featured" />
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {PLATFORMS.map(([k, l]) => (
          <input key={k} className="input-lifted text-sm" placeholder={l} value={f.social_links[k] || ""}
            onChange={(e) => set("social_links", { ...f.social_links, [k]: e.target.value })} data-testid={`mp-social-${k}`} />
        ))}
      </div>
      <Check checked={f.consent_public} onChange={(v) => set("consent_public", v)} testid="mp-consent">I consent to a public showcase listing (requires admin approval).</Check>
      <Check checked={f.show_audience_figures} onChange={(v) => set("show_audience_figures", v)} testid="mp-show-figures">Show my self-reported follower counts.</Check>
      <div className="flex flex-wrap gap-2">
        <button className="btn-lifted text-xs" data-testid="mp-save" onClick={() => act(() => api.patch(`/creators/me/${p.id}`, f), "Saved. Changes to an approved profile go back to review.")}>Save changes</button>
        {hasAffiliate && (
          <button className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="mp-link-affiliate"
            onClick={() => act(() => api.post(`/creators/me/${p.id}/link-affiliate`, p.affiliate_linked ? { unlink: true } : {}), p.affiliate_linked ? "Affiliate unlinked." : "Affiliate identity linked.")}>
            <Link2 className="h-3 w-3" /> {p.affiliate_linked ? "Unlink affiliate" : "Link my affiliate profile"}
          </button>
        )}
        <button className="btn-ghost text-xs inline-flex items-center gap-1 text-rose-300" data-testid="mp-remove"
          onClick={() => window.confirm("Remove this profile from the showcase?") && act(() => api.delete(`/creators/me/${p.id}`), "Profile removed.")}>
          <Trash2 className="h-3 w-3" /> Remove profile
        </button>
      </div>
      {msg && <div className="text-xs text-white/70" data-testid="mp-message">{msg}</div>}
    </div>
  );
}

export function MyCreatorProfiles() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/creators/me").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);
  if (!user || !data || !data.profiles?.length) return null;
  return (
    <section id="my-creator-profile" className="py-12 border-t border-white/5" data-testid="my-creator-profiles">
      <div className="container mx-auto px-4 max-w-3xl space-y-4">
        <div className="flex items-center gap-2"><UserCog className="h-5 w-5 text-lifted-green" />
          <h2 className="font-anton uppercase text-2xl text-white">My Creator Profile</h2></div>
        {data.profiles.map((p) => <ProfileEditor key={p.id + p.updated_at} p={p} hasAffiliate={data.has_affiliate_profile} reload={load} />)}
      </div>
    </section>
  );
}
